import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Paper, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useAppDispatch } from '../../../store/store';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchTeamInPersonVisitsRate, MenuProps, exportTeamInPersonVisitRate } from '../WidgetService';
import EngagementMemberSummary from '../../../Models/Widget/EngagementMemberSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { parentUrl } from '../../../utils/constants';
import { getDateFilter } from '../../HedisMetrics/HedisMetricService';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { dateFormatter } from '../../../utils/timeFormat';
import { fetchTeams } from '../../../store/careTeams.slice';
import TeamDropdown from '../../Shared/TeamDropdown';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';
import { useSearchParams } from 'react-router-dom';
import '../Widgets.css';

const TeamInPersonVisitRateDetail = () => {
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const {
        engagementDateFilter,
        setEngagementDateFilter,
        engagementFromDate,
        setEngagementFromDate,
        engagementToDate,
        setEngagementToDate,
        engagementSelectedPlans,
        setEngagementSelectedPlans,
        engagementSelectedTeam,
        setEngagementSelectedTeam,
        engagementShowInActiveTeams,
        setEngagementShowInActiveTeams,
    } = useWidgetContext();
    const [members, setMembers] = useState<EngagementMemberSummary>();
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const hasElevatedFilterAccess = searchParams.get('hasElevatedFilterAccess') === 'true';

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dateFilterList = getDateFilter();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
        dispatch(fetchTeams(true));
    }, [isFetched, engagementSelectedPlans, dispatch]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        const shouldFetch = engagementDateFilter !== TimePeriodFilterEnum.Custom || (engagementFromDate && engagementToDate);

        if (!shouldFetch) {
            return;
        }

        const changeFilter = async () => {
            setIsMembersLoading(true);
            const data = await fetchTeamInPersonVisitsRate(
                engagementSelectedPlans.join(','),
                engagementSelectedTeam,
                engagementDateFilter,
                engagementFromDate && engagementDateFilter === TimePeriodFilterEnum.Custom ? new Date(engagementFromDate).toISOString() : '',
                engagementToDate && engagementDateFilter === TimePeriodFilterEnum.Custom ? new Date(engagementToDate).toISOString() : '',
                engagementShowInActiveTeams,
                true
            );
            setMembers(data);
            setIsMembersLoading(false);
            setIsFetched(true);
        };

        changeFilter();
    }, [
        engagementSelectedPlans,
        engagementSelectedTeam,
        engagementDateFilter,
        engagementFromDate,
        engagementToDate,
        setIsFetched,
        engagementShowInActiveTeams,
    ]);

    const backToWidget = useCallback(() => {
        history('/provider/metricsummary/smart-dashboard');
    }, [history]);

    const changeFromDate = useCallback(
        (date: any) => {
            setEngagementFromDate(date);
        },
        [setEngagementFromDate]
    );

    const changeToDate = useCallback(
        (date: any) => {
            setEngagementToDate(date);
        },
        [setEngagementToDate]
    );

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportTeamInPersonVisitRate(
            engagementSelectedPlans.join(','),
            engagementSelectedTeam,
            engagementDateFilter,
            engagementFromDate && engagementDateFilter === TimePeriodFilterEnum.Custom ? new Date(engagementFromDate).toISOString() : '',
            engagementToDate && engagementDateFilter === TimePeriodFilterEnum.Custom ? new Date(engagementToDate).toISOString() : '',
            engagementShowInActiveTeams
        );

        const blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const today = new Date();
        const dateInfo = today.toISOString().split('T')[0].replace(/-/g, '');
        link.setAttribute('download', `TeamInPersonVisitRate - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    const getVisitedTextColor = (row: any) => {
        return row.IsLicensedStaffVisited ? 'green' : 'red';
    };

    const getNotVisitedTextColor = (row: any) => {
        return Boolean(row.AppointmentDate) ? (row.IsLicensedStaffVisited ? 'green' : 'red') : 'black';
    };

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Typography color={getVisitedTextColor(params.row)} fontSize={14}>
                        {params.api.getSortedRowIds().indexOf(params.id) + 1}
                    </Typography>
                );
            },
        },
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
            renderCell: (params) => {
                return (
                    <Typography color={getVisitedTextColor(params.row)} fontSize={14}>
                        {params.row.Name}
                    </Typography>
                );
            },
        },
        {
            field: 'DateInfo',
            headerName: 'Visited Date',
            width: 150,
            renderCell: (params) => {
                return (
                    <Typography color={getVisitedTextColor(params.row)} fontSize={14}>
                        {params.row.DateInfo ? dateFormatter(new Date(params.row.DateInfo)) : '-'}
                    </Typography>
                );
            },
        },
        {
            field: 'IsLicensedStaffVisited',
            headerName: 'Is Licensed Staff Visit',
            width: 180,
            renderCell: (params) => {
                return (
                    <Typography color={getVisitedTextColor(params.row)} fontSize={14}>
                        {params.row.IsLicensedStaffVisited === true ? 'Yes' : 'No'}
                    </Typography>
                );
            },
        },
    ];

    const columnList: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14} color={getNotVisitedTextColor(params.row)}>
                        {params.api.getSortedRowIds().indexOf(params.id) + 1}
                    </Typography>
                );
            },
        },
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14} color={getNotVisitedTextColor(params.row)}>
                        {params.row.Name}
                    </Typography>
                );
            },
        },
        {
            field: 'AppointmentDate',
            headerName: 'Next Appointment',
            width: 200,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14} color={getNotVisitedTextColor(params.row)}>
                        {params.row.AppointmentDate}
                    </Typography>
                );
            },
        },
        {
            field: 'IsLicensedStaffVisited',
            headerName: 'Is Licensed Staff Visit',
            width: 180,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14} color={getNotVisitedTextColor(params.row)}>
                        {Boolean(params.row.AppointmentDate) ? (params.row.IsLicensedStaffVisited === true ? 'Yes' : 'No') : ''}
                    </Typography>
                );
            },
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Team In Person Visit Rate
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    {
                        hasElevatedFilterAccess && (<Grid size={1.5}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={engagementSelectedTeam}
                                onChange={(id) => setEngagementSelectedTeam(id)}
                                showInactive={engagementShowInActiveTeams}
                                onToggleInactive={setEngagementShowInActiveTeams}
                            />
                        </Grid>)
                    }
                    <Grid size={1.5}>
                        <HealthPlanMultiSelect
                            value={engagementSelectedPlans}
                            onChange={setEngagementSelectedPlans}
                            menuProps={MenuProps}
                        />
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select
                                value={engagementDateFilter}
                                label="Date Filter"
                                name="Date"
                                onChange={(e) => setEngagementDateFilter(parseInt(e.target.value))}
                            >
                                {dateFilterList.map((date) => (
                                    <MenuItem key={date.Id} value={date.Id}>
                                        {date.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {engagementDateFilter === TimePeriodFilterEnum.Custom && (
                        <>
                            <Grid size={1.5}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={engagementFromDate ?? null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={changeFromDate}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid size={1.5}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={engagementToDate ?? null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={changeToDate}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                        </>
                    )}
                    {engagementDateFilter === TimePeriodFilterEnum.Custom ? <Grid size={3.2}></Grid> : <Grid size={6.2}></Grid>}
                    {
                        !hasElevatedFilterAccess && (
                            <Grid size={1.5}>
                            </Grid>)
                    }
                    <Grid size={1}>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportDetail()}
                            variant="contained"
                            color="success"
                            disabled={members?.EngagementMemberDetails?.length === 0}
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {!isMembersLoading ? (
                            <Grid container columnSpacing={3}>
                                {members?.EngagementMemberDetails?.map((detail, index) => (
                                    <React.Fragment key={`detail-${index}`}>
                                        <Grid size={12} textAlign="center" paddingTop={index === 0 ? 0 : 2}>
                                            <Typography display="inline-block" variant="h5" marginTop={1}>
                                                {detail.Month}
                                            </Typography>
                                            <hr />
                                        </Grid>
                                        <Grid size={6} textAlign="center">
                                            <Typography display="inline-block" variant="h6" color="primary">
                                                Visited ({detail.VisitedMembers.length})
                                            </Typography>
                                            <Grid size={12} sx={{ height: '67vh', width: '100%' }}>
                                                <DataGrid
                                                    columns={columns}
                                                    rows={detail.VisitedMembers}
                                                    getRowId={(row) => row.Id || `${row.ItemId}-${row.DateInfo}`}
                                                    paginationMode="server"
                                                    hideFooterPagination
                                                    hideFooter
                                                    onCellClick={(params: any) => {
                                                        window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid size={6} textAlign="center">
                                            <Typography display="inline-block" variant="h6" color="primary">
                                                Not Visited ({detail.NotVisitedMembers.length})
                                            </Typography>
                                            <Grid size={12} sx={{ height: '67vh', width: '100%' }}>
                                                <DataGrid
                                                    columns={columnList}
                                                    rows={detail.NotVisitedMembers}
                                                    getRowId={(row) => row.Id || row.ItemId}
                                                    paginationMode="server"
                                                    hideFooterPagination
                                                    hideFooter
                                                    onCellClick={(params: any) => {
                                                        window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>
                        ) : (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default TeamInPersonVisitRateDetail;
